import { HTTPError } from 'ky';
import { http } from '@/lib/http';
import { asLockConflict } from '@/lib/locks/api';
import { OfferExpiredError } from './errors';
import type { BookInput, HoldInput, HoldResult, ManualHold, Reservation, ReservationRef } from './types';

/** booking@v2's 409 `offer_expired` → {@link OfferExpiredError} (§6.15); anything else is left to the caller. */
async function asOfferExpired(err: unknown): Promise<void> {
  if (!(err instanceof HTTPError) || err.response.status !== 409) return;
  const body = (await err.response.clone().json().catch(() => null)) as
    | { error?: string; code?: string; offerUrn?: string }
    | null;
  if (body?.error === 'offer_expired' || body?.code === 'offer_expired') {
    throw new OfferExpiredError(typeof body.offerUrn === 'string' ? body.offerUrn : null);
  }
}

// Booking lifecycle client (frozen contract v1). Writes (book/cancel) carry an
// Idempotency-Key the CALLER generates once per logical action and REUSES on
// retry — booking confirm is irreversible, so a fresh key on retry would
// double-book. operator-bff forwards the key as the header.
//
// Auto-lock-on-write-intent: each write auto-acquires the PNR's sub-case lock at
// the BFF (the operator never clicks "lock"). On contention the BFF returns a 409
// `{ error:'locked', holder }` — `asLockConflict` turns it into a LockConflictError
// so the UI shows "PNR x is being edited by <holder>". Passing the optional
// `subCaseUrn` is what lets the BFF lock the right PNR; book always carries one.

/** Soft-hold an offer; the caller keeps the attemptUrn as the live HOLD state. */
export async function holdOffer(input: HoldInput): Promise<HoldResult> {
  try {
    return await http.post('v1/booking/hold', { json: input }).json<HoldResult>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * An unreadable active-hold list is an error, never an empty availability claim —
 * the caller fails CLOSED on it and keeps the accommodation choice disabled.
 *
 * The one exception is a 404: this route is newer than the hold facts it reads, so
 * a BFF or booking that does not serve it also holds no recoverable facts. Treating
 * that as an error would disable "Choose a hotel" forever with no way out, which is
 * a worse failure than the one the gate exists to prevent.
 */
export async function getManualHolds(caseUrn: string, subCaseUrn: string): Promise<ManualHold[]> {
  let page: { items: ManualHold[] };
  try {
    page = await http.get('v1/booking/manual-holds', {
      searchParams: { caseUrn, subCaseUrn },
      timeout: 8_000,
      retry: 0,
    }).json<{ items: ManualHold[] }>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) return [];
    throw err;
  }
  if (!Array.isArray(page?.items) || page.items.some((hold) =>
    !hold || hold.caseUrn !== caseUrn || hold.subCaseUrn !== subCaseUrn ||
    typeof hold.attemptUrn !== 'string' || typeof hold.offerUrn !== 'string' ||
    hold.status !== 'HELD' || typeof hold.expiresAt !== 'string')) {
    throw new Error('Invalid manual hold response');
  }
  return page.items;
}

/** Confirm a held offer (async → PENDING). `idempotencyKey` is stable per attempt. */
export async function bookReservation(input: BookInput, idempotencyKey: string): Promise<ReservationRef> {
  try {
    return await http
      .post('v1/booking/book', { json: input, headers: { 'Idempotency-Key': idempotencyKey } })
      .json<ReservationRef>();
  } catch (err) {
    await asOfferExpired(err);
    return asLockConflict(err);
  }
}

/**
 * Cancel a BOOKED reservation (async → PENDING → CANCELLED). `caseUrn` rides
 * along so the BFF can fan out a CaseChanged WS event to the case room (live
 * cross-user updates) — the booking domain ignores it.
 */
export async function cancelReservation(
  reservationUrn: string,
  reasonCode: string,
  idempotencyKey: string,
  // subCaseUrn → the BFF auto-acquires the PNR's sub-case lock for the write.
  // caseUrn → the BFF fans out a CaseChanged WS event to the case room (live
  // cross-user updates). Both are additive; the booking domain ignores them.
  subCaseUrn?: string,
  caseUrn?: string,
): Promise<ReservationRef> {
  try {
    return await http
      .post('v1/booking/cancel', {
        json: {
          reservationUrn,
          reasonCode,
          ...(subCaseUrn ? { subCaseUrn } : {}),
          ...(caseUrn ? { caseUrn } : {}),
        },
        headers: { 'Idempotency-Key': idempotencyKey },
      })
      .json<ReservationRef>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/** Release a hold before booking (a hold has no reservationUrn). */
export async function releaseHold(
  offerUrn: string,
  caseUrn: string,
  subCaseUrn?: string,
): Promise<{ released: boolean }> {
  try {
    return await http
      .post('v1/booking/hold/release', { json: { offerUrn, caseUrn, ...(subCaseUrn ? { subCaseUrn } : {}) } })
      .json<{ released: boolean }>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/**
 * Release a HELD reservation (operator finding H). NEVER `cancelReservation`
 * for a HELD row — booking's cancel command silently no-ops anything but
 * BOOKED. This drives the per-reservation release twin of the hold-expiry
 * path: the row goes RELEASED/CANCELLED and the inventory is freed exactly as
 * the acceptance-hold expiry frees it; the booking side answers 409 when the
 * row is not HELD any more. `subCaseUrn`/`caseUrn` ride along as on cancel
 * (BFF sub-case lock + CaseChanged WS fan-out).
 */
export async function releaseHeldReservation(
  reservationUrn: string,
  caseUrn?: string,
  subCaseUrn?: string,
): Promise<{ released: boolean }> {
  try {
    return await http
      .post(`v1/booking/reservations/${encodeURIComponent(reservationUrn)}/release`, {
        json: { ...(subCaseUrn ? { subCaseUrn } : {}), ...(caseUrn ? { caseUrn } : {}) },
      })
      .json<{ released: boolean }>();
  } catch (err) {
    return asLockConflict(err);
  }
}

/** The poll target — durable reservations for a case, newest-first. */
export async function getReservations(caseUrn: string): Promise<Reservation[]> {
  return http.get(`v1/cases/${encodeURIComponent(caseUrn)}/reservations`).json<Reservation[]>();
}
